import Dexie, { type EntityTable } from "dexie";

// Local-first store. Every write lands here first; the outbox holds changes
// until the server acknowledges them. Until POST /sync (Phase 4) the outbox is
// flushed with per-resource PUT/DELETE (D34). Rows use the API's wire format
// (snake_case, ISO timestamps) so they round-trip without mapping.

export type TaskStatus = "inbox" | "todo" | "done" | "dropped";

type SyncedRow = {
	id: string;
	created_at: string;
	updated_at: string;
	deleted_at: string | null;
};

export type Task = SyncedRow & {
	project_id: string | null;
	parent_id: string | null;
	title: string;
	notes: string;
	status: TaskStatus;
	priority: number;
	tags: string[];
	/** The day in Africa/Kampala, YYYY-MM-DD. */
	due_date: string | null;
	/** UTC instant; set only when a time was given. */
	due_at: string | null;
	estimate_min: number | null;
	must_win_date: string | null;
	recurrence_rule: string | null;
	completed_at: string | null;
};

export type BlockKind = "task" | "focus" | "study" | "break" | "other";

export type TimeBlock = SyncedRow & {
	task_id: string | null;
	start_at: string;
	end_at: string;
	kind: BlockKind;
	title: string;
};

export type Project = SyncedRow & {
	name: string;
	color: string;
	archived: boolean;
};

export type WalletType = "cash" | "momo" | "bank";

export type Wallet = SyncedRow & {
	name: string;
	type: WalletType;
	/** Whole UGX; may be negative (e.g. an overdraft). */
	opening_balance: number;
	archived: boolean;
};

export type CategoryKind = "income" | "expense";

export type Category = SyncedRow & {
	name: string;
	kind: CategoryKind;
	/** Counts toward the daily allowance (rent and savings usually do not). */
	spendable: boolean;
	parent_id: string | null;
	archived: boolean;
};

/** A savings goal, tracked by hand (D67): `saved_amount` is what was put aside, not a wallet balance. */
export type Goal = SyncedRow & {
	name: string;
	/** Whole UGX, greater than 0. */
	target_amount: number;
	/** Whole UGX, 0 or more; may exceed the target. */
	saved_amount: number;
	/** The Kampala day to reach the target by, YYYY-MM-DD. */
	target_date: string | null;
	archived: boolean;
};

export type IncomeStream = SyncedRow & {
	name: string;
	archived: boolean;
};

export type TransactionKind = "income" | "expense" | "transfer";

export type Transaction = SyncedRow & {
	kind: TransactionKind;
	/** Whole UGX, always positive; `kind` gives the direction. */
	amount: number;
	/** Whole UGX charged on top (mobile-money fees); always spending (D40). */
	fee: number;
	occurred_at: string;
	note: string;
	source: "manual" | "sms" | "csv";
	wallet_id: string;
	/** Set only for transfers. */
	to_wallet_id: string | null;
	category_id: string | null;
	stream_id: string | null;
	task_id: string | null;
};

export type Budget = SyncedRow & {
	/** First day of the month, YYYY-MM-01. */
	month: string;
	category_id: string;
	amount: number;
};

/** A recurring bill (FN-9). Paying it logs an expense and moves next_due on (lib/bills.ts). */
export type Bill = SyncedRow & {
	name: string;
	/** Whole UGX. */
	amount: number;
	wallet_id: string;
	category_id: string | null;
	/** Canonical RRULE subset (D38), e.g. "FREQ=MONTHLY;BYMONTHDAY=31". */
	recurrence_rule: string;
	/** The Kampala day the next payment is due, YYYY-MM-DD. */
	next_due: string;
	archived: boolean;
};

export type Habit = SyncedRow & {
	name: string;
	cadence: "daily" | "weekly";
	/** Check-ins needed per day (daily) or per ISO week (weekly). */
	target_per_period: number;
	/** 0 or 1: missed days forgiven per ISO week (daily habits). */
	grace_days_per_week: number;
	/** Still due on a minimum-viable day (HB-3). */
	is_minimum: boolean;
	archived: boolean;
};

/** One habit on one day. The id is derived from habit_id:date and never deleted (D49). */
export type HabitLog = SyncedRow & {
	habit_id: string;
	date: string;
	count: number;
};

export type ReviewKind = "morning" | "evening" | "weekly";

/** One review per kind and day (weekly: the Monday). Id derived from kind:date (D49). */
export type Review = SyncedRow & {
	kind: ReviewKind;
	date: string;
	intention: string;
	energy: number | null;
	mood: number | null;
	lesson: string;
	score: number | null;
	/** Minimum-viable-day mode, set on the morning review (D50). */
	minimum_day: boolean;
	summary: Record<string, unknown>;
};

export type TrackStatus = "active" | "paused" | "done";

export type Track = SyncedRow & {
	name: string;
	/** Study target in minutes per ISO week; 0 means no target. */
	weekly_target_min: number;
	status: TrackStatus;
};

export type Topic = SyncedRow & {
	track_id: string;
	name: string;
	/** A topic that marks real progress through the track. */
	milestone: boolean;
	done: boolean;
};

/**
 * A focus block. While running, `ended_at` is null and the remaining time is
 * computed from `started_at` and the clock, never ticked (D56).
 */
export type FocusSession = SyncedRow & {
	task_id: string | null;
	track_id: string | null;
	planned_min: number;
	started_at: string;
	ended_at: string | null;
	completed: boolean;
	distraction_count: number;
};

export type Distraction = SyncedRow & {
	focus_session_id: string;
	note: string;
	occurred_at: string;
};

export type StudySession = SyncedRow & {
	track_id: string;
	topic_id: string | null;
	/** The day studied in Africa/Kampala, YYYY-MM-DD. */
	date: string;
	minutes: number;
	takeaway: string;
};

/** ts-fsrs State: 0 new, 1 learning, 2 review, 3 relearning. */
export type CardState = 0 | 1 | 2 | 3;

export type Card = SyncedRow & {
	track_id: string;
	topic_id: string | null;
	front: string;
	back: string;
	state: CardState;
	due_at: string;
	stability: number;
	difficulty: number;
	elapsed_days: number;
	scheduled_days: number;
	learning_steps: number;
	reps: number;
	lapses: number;
	last_review: string | null;
	archived: boolean;
};

/** Append-only history with random ids, so two devices keep both rows (D58). */
export type CardReview = SyncedRow & {
	card_id: string;
	/** 1 again, 2 hard, 3 good, 4 easy. */
	rating: 1 | 2 | 3 | 4;
	reviewed_at: string;
	scheduled_days: number;
};

/** Tables that sync with the server; the value is the API path. */
export const syncedTables = {
	tasks: "/tasks",
	time_blocks: "/blocks",
	projects: "/projects",
	wallets: "/wallets",
	categories: "/categories",
	income_streams: "/income-streams",
	transactions: "/transactions",
	budgets: "/budgets",
	bills: "/bills",
	goals: "/goals",
	habits: "/habits",
	habit_logs: "/habit-logs",
	reviews: "/reviews",
	tracks: "/tracks",
	topics: "/topics",
	focus_sessions: "/focus-sessions",
	distractions: "/distractions",
	study_sessions: "/study-sessions",
	cards: "/cards",
	card_reviews: "/card-reviews",
} as const;

export type SyncedTable = keyof typeof syncedTables;

/** The row type stored in each synced table. */
export type SyncedRows = {
	tasks: Task;
	time_blocks: TimeBlock;
	projects: Project;
	wallets: Wallet;
	categories: Category;
	income_streams: IncomeStream;
	transactions: Transaction;
	budgets: Budget;
	bills: Bill;
	goals: Goal;
	habits: Habit;
	habit_logs: HabitLog;
	reviews: Review;
	tracks: Track;
	topics: Topic;
	focus_sessions: FocusSession;
	distractions: Distraction;
	study_sessions: StudySession;
	cards: Card;
	card_reviews: CardReview;
};

export type OutboxOp = "upsert" | "delete";

export type OutboxEntry = {
	id: number;
	table: SyncedTable;
	/** Client-generated UUID of the changed row. */
	entityId: string;
	op: OutboxOp;
	payload: unknown;
	/** ISO timestamp; the newer updated_at wins on the server. */
	updatedAt: string;
	attempts: number;
};

export type MetaEntry = { key: string; value: unknown };

export class MosDB extends Dexie {
	outbox!: EntityTable<OutboxEntry, "id">;
	meta!: EntityTable<MetaEntry, "key">;
	tasks!: EntityTable<Task, "id">;
	time_blocks!: EntityTable<TimeBlock, "id">;
	projects!: EntityTable<Project, "id">;
	wallets!: EntityTable<Wallet, "id">;
	categories!: EntityTable<Category, "id">;
	income_streams!: EntityTable<IncomeStream, "id">;
	transactions!: EntityTable<Transaction, "id">;
	budgets!: EntityTable<Budget, "id">;
	bills!: EntityTable<Bill, "id">;
	goals!: EntityTable<Goal, "id">;
	habits!: EntityTable<Habit, "id">;
	habit_logs!: EntityTable<HabitLog, "id">;
	reviews!: EntityTable<Review, "id">;
	tracks!: EntityTable<Track, "id">;
	topics!: EntityTable<Topic, "id">;
	focus_sessions!: EntityTable<FocusSession, "id">;
	distractions!: EntityTable<Distraction, "id">;
	study_sessions!: EntityTable<StudySession, "id">;
	cards!: EntityTable<Card, "id">;
	card_reviews!: EntityTable<CardReview, "id">;

	constructor(name = "mos") {
		super(name);
		this.version(1).stores({
			outbox: "++id, table, entityId, updatedAt",
			meta: "key",
		});
		this.version(2).stores({
			outbox: "++id, table, entityId, updatedAt, [table+entityId]",
			tasks: "id, status, due_date, must_win_date, updated_at",
			time_blocks: "id, start_at, task_id",
			projects: "id",
		});
		this.version(3).stores({
			tasks: "id, status, due_date, must_win_date, updated_at, parent_id",
		});
		this.version(4).stores({
			wallets: "id",
			categories: "id, kind",
			income_streams: "id",
			transactions: "id, occurred_at, wallet_id, to_wallet_id, category_id",
			budgets: "id, month",
		});
		this.version(5).stores({
			bills: "id, next_due",
		});
		this.version(6).stores({
			habits: "id",
			habit_logs: "id, habit_id, date",
			reviews: "id, [kind+date], date",
		});
		this.version(7).stores({
			tracks: "id, status",
			topics: "id, track_id",
			focus_sessions: "id, started_at, task_id",
			distractions: "id, focus_session_id",
			study_sessions: "id, track_id, date",
			cards: "id, track_id, due_at, state",
			card_reviews: "id, card_id, reviewed_at",
		});
		this.version(8).stores({
			goals: "id",
		});
	}
}

export const db = new MosDB();

export function newId(): string {
	return crypto.randomUUID();
}

/**
 * Queues a change. Payloads carry the whole row, so a newer change replaces
 * any unsent upsert for the same row, and a delete drops it: a row created
 * and deleted offline then costs one DELETE (a 404 the flusher ignores).
 * Call inside a transaction that includes `outbox` to keep it atomic with
 * the row write.
 */
export async function enqueue(
	entry: Omit<OutboxEntry, "id" | "attempts" | "updatedAt"> & { updatedAt?: string },
	database: MosDB = db,
): Promise<number> {
	await database.outbox
		.where("[table+entityId]")
		.equals([entry.table, entry.entityId])
		.filter((e) => e.op === "upsert")
		.delete();
	return database.outbox.add({
		...entry,
		updatedAt: entry.updatedAt ?? new Date().toISOString(),
		attempts: 0,
	});
}

export function pendingCount(database: MosDB = db): Promise<number> {
	return database.outbox.count();
}

export async function hasPending(
	table: SyncedTable,
	entityId: string,
	database: MosDB = db,
): Promise<boolean> {
	return (await database.outbox.where("[table+entityId]").equals([table, entityId]).count()) > 0;
}

const ID_NAMESPACE = "6f3c2b1a-9d4e-4f8a-b7c6-2e5d1a0f9c3b";

/**
 * A stable UUID (version 5 layout, SHA-1) for a natural key such as
 * `habit_id:date`, so two offline devices writing the same day produce the
 * same row and "newer wins" applies instead of a unique violation (D49).
 */
export async function stableId(key: string): Promise<string> {
	const ns = ID_NAMESPACE.replace(/-/g, "");
	const nsBytes = new Uint8Array(ns.match(/../g)!.map((h) => parseInt(h, 16)));
	const name = new TextEncoder().encode(key);
	const data = new Uint8Array(nsBytes.length + name.length);
	data.set(nsBytes);
	data.set(name, nsBytes.length);
	const hash = new Uint8Array(await crypto.subtle.digest("SHA-1", data)).slice(0, 16);
	hash[6] = (hash[6] & 0x0f) | 0x50;
	hash[8] = (hash[8] & 0x3f) | 0x80;
	const hex = [...hash].map((b) => b.toString(16).padStart(2, "0")).join("");
	return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
